import React, { useState } from 'react';
import { Formik } from 'formik';
import { useNavigate } from 'react-router-dom';

import { View, TextInput, Logo, Button2 as Button, FormErrorMessage } from '../components';
import { Images, Colors, auth } from '../config2';
import { useTogglePasswordVisibility } from '../hooks';
import { signupValidationSchema } from '../utils';
import { en, es } from '../localizations';
import ContractService from '../services/ContractService';
import { createUserWithEmailAndPassword } from 'firebase/auth';

const i18n = {
  t: (key: string) => {
    const translations = es as any;
    return translations[key] || key;
  }
};

export const SignupScreen = () => {
  const navigate = useNavigate();
  const [errorState, setErrorState] = useState('');

  const {
    passwordVisibility,
    handlePasswordVisibility,
    rightIcon,
    handleConfirmPasswordVisibility,
    confirmPasswordIcon,
    confirmPasswordVisibility
  } = useTogglePasswordVisibility();

  const handleSignup = async (values: { email: string, password: string, contractId: string }) => {
    const { email, password, contractId } = values;
    try {
      const isValidContract = await ContractService.isValidContract(contractId);

      if (isValidContract) {
        await createUserWithEmailAndPassword(auth, email, password);
        await ContractService.updateUsers(contractId, email);
      } else {
        setErrorState(i18n.t('contractNoExist'));
      }
    } catch (error: any) {
      if (error.code === 'auth/email-already-in-use') {
        setErrorState(i18n.t('userAlreadyExist'));
      } else {
        setErrorState(i18n.t(error.response.data.code));
      }
    }
  };

  return (
    <View style={{ flex: 1, backgroundColor: Colors.white, paddingLeft: '12px', paddingRight: '12px', minHeight: '100vh' }}>
      <div style={{ overflowY: 'auto', flex: 1 }}>
        <View style={{ display: 'flex', alignItems: 'center' }}>
          <Logo uri={Images.logo} />
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: Colors.black, paddingTop: '20px' }}>{i18n.t('createNewAccount')}</h1>
        </View>
        {/* Formik Wrapper */}
        <Formik
          initialValues={{
            email: '',
            password: '',
            confirmPassword: '',
            contractId: ''
          }}
          validationSchema={signupValidationSchema}
          onSubmit={values => handleSignup(values)}
        >
          {({
            values,
            touched,
            errors,
            handleChange,
            handleSubmit,
            handleBlur
          }) => (
            <>
              {/* Input fields */}
              <TextInput
                name='email'
                leftIconName='email'
                placeholder={i18n.t('mail')}
                type='email'
                autoFocus={true}
                value={values.email}
                onChange={handleChange('email')}
                onBlur={handleBlur('email')}
              />

              <FormErrorMessage error={errors.email} visible={touched.email} />
              <TextInput
                name='contract'
                leftIconName='file-sign'
                placeholder={i18n.t('contractId')}
                type='text'
                value={values.contractId}
                onChange={handleChange('contractId')}
                onBlur={handleBlur('contractId')}
              />
              <FormErrorMessage
                error={errors.contractId}
                visible={touched.contractId}
              />
              <TextInput
                name='password'
                leftIconName='key-variant'
                placeholder={i18n.t('password')}
                type={passwordVisibility ? 'password' : 'text'}
                rightIcon={rightIcon}
                handlePasswordVisibility={handlePasswordVisibility}
                value={values.password}
                onChange={handleChange('password')}
                onBlur={handleBlur('password')}
              />
              <FormErrorMessage
                error={errors.password}
                visible={touched.password}
              />
              <TextInput
                name='confirmPassword'
                leftIconName='key-variant'
                placeholder={i18n.t('repeatPassword')}
                type={confirmPasswordVisibility ? 'password' : 'text'}
                rightIcon={confirmPasswordIcon}
                handlePasswordVisibility={handleConfirmPasswordVisibility}
                value={values.confirmPassword}
                onChange={handleChange('confirmPassword')}
                onBlur={handleBlur('confirmPassword')}
              />
              <FormErrorMessage
                error={errors.confirmPassword}
                visible={touched.confirmPassword}
              />
              {/* Display Screen Error Mesages */}
              {errorState !== '' ? (
                <FormErrorMessage error={errorState} visible={true} />
              ) : null}
              <button
                style={{
                  width: '100%',
                  marginTop: '8px',
                  backgroundColor: Colors.brown_border,
                  padding: '10px',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '20px',
                  color: Colors.white,
                  fontWeight: '700',
                }}
                onClick={() => handleSubmit()}
              >
                {i18n.t('signUp')}
              </button>
            </>
          )}
        </Formik>
        <Button
          style={{ marginTop: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          borderless
          title={i18n.t('alreadyHaveAccount')}
          onPress={() => navigate('/login')}
        />
      </div>
    </View>
  );
};
